import { useMemo } from "react";
import { API_ENDPOINT, isServerApiError } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";

const EMPTY_SET: ReadonlySet<number> = new Set();

/**
 * Fetches the set of epoch ids the connected hub user has a leaderboard
 * record in for the given TTE campaign. Used to power the "Only my epochs"
 * filter in the epoch selector — the FE treats the response as a pure
 * `Set<number>` overlay against the existing `epochItems` list.
 *
 * Disabled when unauthenticated: there is nothing to scope to until a hub
 * user identity is resolved. Runtime validation of the response is handled
 * by `ApiHelper` via the registered `ENDPOINT_SCHEMAS` entry — no need for
 * a second `safeParse` here.
 */
export const useMyLeaderboardEpochs = (campaignName: string | undefined) => {
  const { hubUser, authenticated, auth, isAuthStateStable } = useHubUser();
  const apiHelper = useApi();

  const enabled = !!campaignName && authenticated && isAuthStateStable && hubUser?.id != null && !!auth;

  const { data, isLoading } = useQuery({
    queryKey: ["my-leaderboard-epochs", campaignName, hubUser?.id],
    queryFn: async () => {
      if (!auth || !campaignName) return [] as number[];

      let response;
      try {
        response = await apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_LEADERBOARD_MY_EPOCHS, {
          queryParams: { campaign: campaignName },
        });
      } catch (error) {
        const statusCode = (error as { statusCode?: number }).statusCode;
        if (statusCode === 404 || statusCode === 403) return [] as number[];
        throw error;
      }

      if (isServerApiError(response)) {
        if (response.statusCode === 404 || response.statusCode === 403) return [] as number[];
        throw new Error(response.message);
      }

      return response.epochIds ?? [];
    },
    enabled,
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const myEpochIdSet = useMemo<ReadonlySet<number>>(() => {
    if (!data) return EMPTY_SET;
    return new Set(data);
  }, [data]);

  return {
    myEpochIdSet,
    isLoading,
    hasAny: myEpochIdSet.size > 0,
  };
};
